import React, { useEffect, useRef, useState } from 'react';
import './priority.style.scss';
import { __PriorityProps, __PriorityItem, __PriorityRef } from './priority.interface';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { TextDirection } from 'interface';
import HintText from '../hintText/hintText.index';
import QuestionImage from '../questionImage/questionImage.index';
import { extractTitle } from 'scripts/string';

export interface SLPPriorityRef extends __PriorityRef {}
export interface PriorityItem extends __PriorityItem {}

const SLPPriority: React.ForwardRefRenderFunction<__PriorityRef, __PriorityProps> = (props: __PriorityProps, ref) => {
    const [value, setValue] = useState<__PriorityItem[]>();
    const immediateValue = useRef<__PriorityItem[] | undefined>(value);
    const [error, setError] = useState<string>();
    const labelRef = useRef<HTMLLabelElement>(null);

    useEffect(() => setError(undefined), [value]);

    React.useImperativeHandle(ref, () => ({
        getValue: () => (immediateValue.current ? immediateValue.current : []),
        setError: (text) => {
            setError(text);
        },
    }));

    const changeHandler = (e: __PriorityItem[]) => {
        immediateValue.current = e;
        setValue(immediateValue.current);
        setError(undefined);
        if (props.onChange) props.onChange(value);
    };

    useEffect(() => {
        if (JSON.stringify(props.initialItems) !== JSON.stringify(value)) {
            immediateValue.current = props.initialItems;
            setValue(immediateValue.current);
        }
    }, [props.initialItems]);

    useEffect(() => {
        if (labelRef.current && props.label) labelRef.current.innerHTML = extractTitle(props.label, false);
    }, [props.label]);

    function onDragEnd(result: DropResult) {
        if (result.destination && value) {
            const temp = [...value];
            const sourceOrder = temp[result.source.index].value;
            const destinationOrder = temp[result.destination.index].value;
            value.map((i, index) => {
                if (sourceOrder > destinationOrder) {
                    if (i.value >= destinationOrder && i.value <= sourceOrder) temp[index].value = i.value + 1;
                } else {
                    if (i.value >= sourceOrder && i.value <= destinationOrder) temp[index].value = i.value - 1;
                }
            });
            temp[result.source.index].value = destinationOrder;
            changeHandler([...temp.sort((a, b) => Number(a.value) - Number(b.value))]);
        }
    }

    function onClickArrow(item: __PriorityItem, dir: 'up' | 'down') {
        if (value) {
            const temp = value;
            const index = temp.findIndex((i) => i.payload === item.payload);
            if (index !== undefined && index > -1) {
                const sourceOrder = temp[index].value;
                const destinationOrder = temp[dir === 'up' ? index - 1 : index + 1].value;
                temp[index].value = destinationOrder;
                temp[dir === 'up' ? index - 1 : index + 1].value = sourceOrder;
                changeHandler([...temp.sort((a, b) => Number(a.value) - Number(b.value))]);
            }
        }
    }

    return (
        <div className={`u-check-SLP-priority ${props.dir === TextDirection.rtl ? 'rtl-text' : ''}`}>
            <div className="header-title" style={{ color: props.labelColor }}>
                {props.required && <span className="required-star">*</span>}
                <span className="counter-span">{props.counter ? props.counter + '- ' : ''}</span>
                <label ref={labelRef} style={{ color: props.labelColor }} />
            </div>
            <QuestionImage src={props.image?.file} />
            <HintText value={props.hint} />
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="optionsList">
                    {(dropProvided) => (
                        <div
                            ref={dropProvided.innerRef}
                            className="cards-container"
                            style={{
                                border: `${props.color ? props.color + ' dashed 1px ' : ''}`,
                                backgroundColor: `${
                                    props.color ? (props.color.length > 4 ? props.color + '30' : props.color + '3') : ''
                                }`,
                            }}
                        >
                            {value
                                ?.sort((a, b) => a.value - b.value)
                                .map((item, index) => (
                                    <Draggable key={item.payload} draggableId={item.payload.toString()} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps}>
                                                <span {...provided.dragHandleProps} className="material-icons-outlined">
                                                    drag_indicator
                                                </span>
                                                <label
                                                    className={`${item.image ? 'has-image' : ''}`}
                                                    style={{ color: props.color }}
                                                >
                                                    {item.image && (
                                                        <div className="image-cover">
                                                            <div>
                                                                <img src={item.image.file} />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <span dangerouslySetInnerHTML={{ __html: item.title }}></span>
                                                </label>
                                                <div className="d-flex flex-column">
                                                    <span
                                                        onClick={() => onClickArrow(item, 'up')}
                                                        className={`material-icons-outlined ${
                                                            index === 0 ? 'disabled' : ''
                                                        } `}
                                                    >
                                                        expand_less
                                                    </span>
                                                    <span
                                                        onClick={() => onClickArrow(item, 'down')}
                                                        className={`material-icons-outlined ${
                                                            index === value.length - 1 ? 'disabled' : ''
                                                        } `}
                                                    >
                                                        expand_more
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                            {dropProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {error && <p className="pri-error">{error}</p>}
        </div>
    );
};

export default React.forwardRef(SLPPriority);
